import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import HomeIcon from 'assets/nav/home.svg';
import BenefitsIcon from 'assets/nav/wallet.svg';
import GetCareIcon from 'assets/nav/get-care.svg';
import SupportIcon from 'assets/nav/support.svg';
import JourneyIcon from 'assets/nav/journey.svg';
import HomeSelectedIcon from 'assets/nav/home-selected.svg';
import BenefitsSelectedIcon from 'assets/nav/wallet-selected.svg';
import GetCareSelectedIcon from 'assets/nav/get-care-selected.svg';
import JourneySelectedIcon from 'assets/nav/journey-selected.svg';
import SupportSelectedIcon from 'assets/nav/support-selected.svg';
import {
  APP_MANAGED_ANCHORS,
  APP_MANAGED_ROUTES,
} from 'common/app-managed-routes';
import { NAV_ANALYTICS_DATA } from 'common/nav-analytics-data';
import { NavigationAnalytics } from 'types/highmark-common.types';
import { useGetScreenForAnalytics } from './use-get-screen-for-analytics.hook';

export enum NavIds {
  ACTIVITIES = 'ACTIVITIES',
  HOME = 'HOME',
  BENEFITS = 'BENEFITS',
  CARE = 'GET_CARE',
  JOURNEY = 'JOURNEY',
  SUPPORT = 'SUPPORT',
  INSURANCE_COVERAGE = 'INSURANCE_COVERAGE',
  HEALTH_AND_WELLNESS = 'HEALTH_AND_WELLNESS',
  CLAIMS = 'CLAIMS',
  ADDITIONAL_BENEFITS = 'ADDITIONAL_BENEFITS',
  GET_CARE = 'GET_CARE',
  ACCESS_CARE_NOW = 'ACCESS_CARE_NOW',
  COMPLETE_YOUR_HEALTH_ASSESSMENT = 'COMPLETE_YOUR_HEALTH_ASSESSMENT',
  WELLNESS_REWARDS_PROGRAM = 'WELLNESS_REWARDS_PROGRAM',
  CONNECT_WITH_US = 'CONNECT_WITH_US',
  FORMS_AND_FILES = 'FORMS_AND_FILES',
  FIND_AND_PLAN_CARE = 'FIND_AND_PLAN_CARE',
  FAQ_GLOSSARY = 'FAQ_GLOSSARY',
  ABOUT_ME = 'HEALTH_PROFILE',
  PROGRESS = 'PROGRESS',
  HISTORY = 'HISTORY',
  EXPLORE = 'EXPLORE',
}

export enum LinkType {
  NAV = 'navLink',
  LINK = 'link',
  SUB_MENU = 'subMenu',
}

export type NavItem = {
  to:
    | string
    | {
        search: string;
        pathname: string;
        state: unknown;
        hash: string;
        key?: string | undefined;
      };
};

export interface NavLink extends NavItem {
  message_id: NavIds;
  type: LinkType;
  icon?: string;
  iconSelected?: string;
  subMenu?: {
    items: NavLink[];
  };
  text?: string;
  analytics?: NavigationAnalytics;
}

// TODO [HMRK-15478] remove enableNavbarEnhancements once navigation enhancements are released.
export const useNavLinksList = (enableNavbarEnhancements?: boolean) => {
  const currentScreen = useGetScreenForAnalytics();
  const benefitsEntryPath = getModulePath(LEAGUE_MODULE_NAMES.wallet);
  const journeyEntryPath = getModulePath(LEAGUE_MODULE_NAMES.healthJourney);
  const careEntryPath = getModulePath(LEAGUE_MODULE_NAMES.careDiscovery);
  const supportEntryPath = APP_MANAGED_ROUTES.SUPPORT;

  const enhancedNavLinksList: NavLink[] = [
    {
      to: '/home',
      message_id: NavIds.HOME,
      type: LinkType.NAV,
      icon: HomeIcon,
      iconSelected: HomeSelectedIcon,
      analytics: {
        ...NAV_ANALYTICS_DATA.MAIN_TABS.HOME,
        screen_name: currentScreen,
      },
    },
    {
      to: benefitsEntryPath,
      message_id: NavIds.BENEFITS,
      type: LinkType.SUB_MENU,
      icon: BenefitsIcon,
      iconSelected: BenefitsSelectedIcon,
      analytics: {
        ...NAV_ANALYTICS_DATA.MAIN_TABS.BENEFITS,
        screen_name: currentScreen,
      },
      subMenu: {
        items: [
          {
            to: `/claims`,
            message_id: NavIds.CLAIMS,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.BENEFITS.CLAIMS,
              screen_name: currentScreen,
            },
          },
          {
            to: `${benefitsEntryPath}#${APP_MANAGED_ANCHORS.BENEFITS.INSURANCE_COVERAGE}`,
            message_id: NavIds.INSURANCE_COVERAGE,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.BENEFITS.INSURANCE_COVERAGE,
              screen_name: currentScreen,
            },
          },

          {
            to: `${benefitsEntryPath}#${APP_MANAGED_ANCHORS.BENEFITS.ADDITIONAL_BENEFITS}`,
            message_id: NavIds.ADDITIONAL_BENEFITS,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.BENEFITS.ADDITIONAL_BENEFITS,
              screen_name: currentScreen,
            },
          },
        ],
      },
    },
    {
      to: careEntryPath,
      message_id: NavIds.CARE,
      type: LinkType.SUB_MENU,
      icon: GetCareIcon,
      iconSelected: GetCareSelectedIcon,
      analytics: {
        ...NAV_ANALYTICS_DATA.MAIN_TABS.GET_CARE,
        screen_name: currentScreen,
      },
      subMenu: {
        items: [
          {
            to: `${careEntryPath}#${APP_MANAGED_ANCHORS.CARE.FIND_AND_PLAN_CARE}`,
            message_id: NavIds.FIND_AND_PLAN_CARE,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.GET_CARE.FIND_AND_PLAN_CARE,
              screen_name: currentScreen,
            },
          },
          {
            to: `${careEntryPath}#${APP_MANAGED_ANCHORS.CARE.ACCESS_CARE_NOW}`,
            message_id: NavIds.ACCESS_CARE_NOW,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.GET_CARE.ACCESS_CARE_NOW,
              screen_name: currentScreen,
            },
          },
        ],
      },
    },
    {
      to: journeyEntryPath,
      message_id: NavIds.JOURNEY,
      type: LinkType.SUB_MENU,
      icon: JourneyIcon,
      iconSelected: JourneySelectedIcon,
      analytics: {
        ...NAV_ANALYTICS_DATA.MAIN_TABS.JOURNEY,
        screen_name: currentScreen,
      },
      subMenu: {
        items: [
          {
            to: LEAGUE_MODULE_NAMES.aboutMe,
            message_id: NavIds.ABOUT_ME,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.JOURNEY.ABOUT_ME,
              screen_name: currentScreen,
            },
          },
          {
            to: LEAGUE_MODULE_NAMES.healthJourney,
            message_id: NavIds.ACTIVITIES,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.JOURNEY.ACTIVITIES,
              screen_name: currentScreen,
            },
          },
          {
            to: LEAGUE_MODULE_NAMES.healthProgress,
            message_id: NavIds.PROGRESS,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.JOURNEY.PROGRESS,
              screen_name: currentScreen,
            },
          },
          {
            to: APP_MANAGED_ROUTES.HEALTH_HISTORY,
            message_id: NavIds.HISTORY,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.JOURNEY.HISTORY,
              screen_name: currentScreen,
            },
          },
          {
            to: LEAGUE_MODULE_NAMES.healthPrograms,
            message_id: NavIds.EXPLORE,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.JOURNEY.EXPLORE,
              screen_name: currentScreen,
            },
          },
        ],
      },
    },
    {
      to: supportEntryPath,
      message_id: NavIds.SUPPORT,
      type: LinkType.SUB_MENU,
      icon: SupportIcon,
      iconSelected: SupportSelectedIcon,
      analytics: {
        ...NAV_ANALYTICS_DATA.MAIN_TABS.SUPPORT,
        screen_name: currentScreen,
      },
      subMenu: {
        items: [
          {
            to: `${supportEntryPath}#${APP_MANAGED_ANCHORS.SUPPORT.NEED_HELP}`,
            message_id: NavIds.CONNECT_WITH_US,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.SUPPORT.CONNECT_WITH_US,
              screen_name: currentScreen,
            },
          },
          {
            to: `${supportEntryPath}#${APP_MANAGED_ANCHORS.SUPPORT.FORMS_AND_FILES}`,
            message_id: NavIds.FORMS_AND_FILES,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.SUPPORT.FORMS_AND_FILES,
              screen_name: currentScreen,
            },
          },
          {
            to: `${supportEntryPath}#${APP_MANAGED_ANCHORS.SUPPORT.FAQ_GLOSSARY}`,
            message_id: NavIds.FAQ_GLOSSARY,
            type: LinkType.LINK,
            analytics: {
              ...NAV_ANALYTICS_DATA.SUBMENUS.SUPPORT.FAQ_GLOSSARY,
              screen_name: currentScreen,
            },
          },
        ],
      },
    },
  ];

  // TODO [HMRK-15478] remove legacyNavLinksList once navigation enhancements are released.
  const legacyNavLinksList: NavLink[] = [
    {
      to: '/home',
      message_id: NavIds.HOME,
      type: LinkType.NAV,
      icon: HomeIcon,
      iconSelected: HomeSelectedIcon,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.wallet),
      message_id: NavIds.BENEFITS,
      type: LinkType.NAV,
      icon: BenefitsIcon,
      iconSelected: BenefitsSelectedIcon,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.careDiscovery),
      message_id: NavIds.CARE,
      type: LinkType.NAV,
      icon: GetCareIcon,
      iconSelected: GetCareSelectedIcon,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
      message_id: NavIds.JOURNEY,
      type: LinkType.NAV,
      icon: JourneyIcon,
      iconSelected: JourneySelectedIcon,
    },
    {
      to: APP_MANAGED_ROUTES.SUPPORT,
      message_id: NavIds.SUPPORT,
      type: LinkType.NAV,
      icon: SupportIcon,
      iconSelected: SupportSelectedIcon,
    },
  ];

  return {
    navLinks: enableNavbarEnhancements
      ? enhancedNavLinksList
      : legacyNavLinksList,
  };
};
